import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionTemplate, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { MaskLines } from "../../components/motion";
import { Sprout } from "../../components/BrandShapes";
import { introState } from "../../components/Intro";
import { ease } from "../../lib/content";

const SLIDES = [
  ["/images/hero-mite.jpg", "Predatory mite Neoseiulus cucumeris on a leaf", "Neoseiulus cucumeris — predatory mite"],
  ["/images/hero-covercrop-alt.jpg", "Flowering phacelia cover crop", "Phacelia cover crop"],
  ["/images/hero-petri.jpg", "Beauveria bassiana culture in a petri dish", "Biopesticide"],
];

const Slides = ({ d, spotlight }) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % SLIDES.length), 5000);
    return () => clearInterval(t);
  }, []);
  return (
    <>
      {SLIDES.map(([src, alt], i) => (
        <motion.img
          key={src}
          src={src}
          alt={alt}
          data-testid={`hero-slide-${i}`}
          className="absolute inset-0 h-full w-full object-cover"
          initial={{ opacity: i === 0 ? 1 : 0, scale: 1.35 }}
          animate={{ opacity: i === idx ? 1 : 0, scale: i === idx ? 1.05 : 1.15 }}
          transition={{ opacity: { duration: 1.2, ease }, scale: { duration: i === idx ? 6 : 1.2, ease } }}
        />
      ))}
      <motion.div className="absolute inset-0" style={{ background: spotlight }} />
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-teal-ink/85 to-transparent p-6 pt-24 text-right">
        <p data-testid="hero-slide-caption" className="font-mono text-[11px] uppercase tracking-[0.18em] text-mist/90">{SLIDES[idx][2]}</p>
        <div className="mt-3 flex justify-end gap-1.5">
          {SLIDES.map(([src], i) => (
            <button
              key={src}
              onClick={() => setIdx(i)}
              aria-label={`Show photo ${i + 1}`}
              data-testid={`hero-slide-dot-${i}`}
              className={`h-1 rounded-full transition-[width,background-color] duration-500 ${i === idx ? "w-8 bg-sage" : "w-3 bg-mist/50"}`}
            />
          ))}
        </div>
      </div>
    </>
  );
};

const Seal = () => (
  <div className="absolute -right-4 -top-6 z-20 h-28 w-28 sm:-right-8 sm:h-32 sm:w-32">
    <svg viewBox="0 0 100 100" className="h-full w-full animate-spinslow text-mist">
      <defs>
        <path id="seal" d="M50 50m-38 0a38 38 0 1 1 76 0a38 38 0 1 1 -76 0" />
      </defs>
      <text fontSize="7.2" letterSpacing="1.6" fill="currentColor" fontFamily="Jost">
        <textPath href="#seal">BIOLOGICAL CONTROL · AGROECOLOGY · IPM · </textPath>
      </text>
    </svg>
    <div className="absolute inset-0 m-auto flex h-14 w-14 items-center justify-center rounded-full bg-sage text-teal-ink">
      <Sprout className="h-7 w-7" strokeWidth={11} />
    </div>
  </div>
);

export const Hero = () => {
  const ref = useRef(null);
  const d = introState.pending ? 3.2 : 0.15;
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const frameY = useTransform(scrollYProgress, [0, 1], [0, -110]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 110, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 110, damping: 18 });
  const sx = useTransform(mx, [-0.5, 0.5], [15, 85]);
  const sy = useTransform(my, [-0.5, 0.5], [10, 90]);
  const spotlight = useMotionTemplate`radial-gradient(circle at ${sx}% ${sy}%, rgba(228,226,221,0.28), transparent 45%)`;

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <section ref={ref} data-testid="home-hero" onMouseMove={onMove} onMouseLeave={onLeave} className="grain relative min-h-[100svh] overflow-hidden bg-teal text-mist">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <img src="/images/aerial.jpg" alt="" className="h-[120%] w-full object-cover opacity-[0.16] mix-blend-luminosity" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-teal/40 via-transparent to-teal" />

      <div className="relative mx-auto grid max-w-[1440px] gap-14 px-5 pb-24 pt-32 sm:px-8 sm:pt-40 lg:grid-cols-12 lg:gap-8 lg:px-12 lg:pb-28 lg:pt-44">
        <div className="lg:col-span-7">
          <motion.p className="label-caps flex items-center gap-4 !text-sm text-sage sm:!text-base" initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.9, ease, delay: d }}>
            <span className="h-px w-10 bg-sage" /> Agroecology · Crop protection
          </motion.p>
          <MaskLines
            as="h1"
            onLoad
            testId="hero-heading"
            delay={d + 0.1}
            stagger={0.13}
            lines={["Crop protection,", "grown from", <span key="l" className="text-sage">living systems.</span>]}
            className="mt-8 font-display text-[2.55rem] font-normal leading-[0.95] tracking-[-0.03em] sm:text-7xl lg:text-[6.6rem] xl:text-[7.2rem]"
          />
          <motion.p
            className="mt-10 max-w-xl text-base leading-relaxed text-mist/85 sm:text-lg"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease, delay: d + 0.6 }}
          >
            Integrated pest management, biological control and functional biodiversity, turned into practical, science-based solutions for growers and their partners.
          </motion.p>
          <motion.div className="mt-10 flex flex-wrap gap-3" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease, delay: d + 0.75 }}>
            <Link to="/products" data-testid="hero-products-btn" className="group label-caps inline-flex items-center gap-3 rounded-full bg-mist px-7 py-4 !text-[11px] text-teal transition-colors duration-300 hover:bg-sage">
              Our products <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
            </Link>
            <Link to="/consulting" data-testid="hero-consulting-btn" className="group label-caps inline-flex items-center gap-3 rounded-full border border-mist/40 px-7 py-4 !text-[11px] transition-colors duration-300 hover:border-mist hover:bg-mist/10">
              Consulting <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
            </Link>
          </motion.div>
        </div>

        <div className="relative mx-auto w-full max-w-[22rem] sm:max-w-sm lg:col-span-5 lg:mx-0 lg:ml-auto lg:max-w-[26rem]" style={{ perspective: 1200 }}>
          <motion.div style={{ y: frameY, rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative">
            <div className="arch absolute -inset-4 border border-sage/40" style={{ transform: "translateZ(-40px)" }} />
            <motion.div
              data-testid="hero-arch-frame"
              className="arch relative aspect-[3/4] overflow-hidden bg-teal-deep"
              initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
              animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
              transition={{ duration: 1.4, ease, delay: d + 0.2 }}
            >
              <Slides d={d} spotlight={spotlight} />
            </motion.div>
            <Seal />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
